import extend from 'extend';
import mapScopeItem from './lib/mapScopeItem';
import mapFinalRepair from './lib/mapFinalRepair';
import * as repairsUtils from '../../lib/repairs';
import { retailerSettingsService } from '../retailer-settings/retailer-settings';
import { returnItemsService } from '../return-items-service/return-items-service';

const blindRepairSidebarFunction = () => {
  const { blindRepairsConfig = {} } = retailerSettingsService.getSettings();
  const node = document.createElement('div');
  document.body.appendChild(node);
  let updateCallback = null;
  let customerFileUpload;

  let sidebarState = {
    config: blindRepairsConfig,
    open: false,
    loading: true,
    currentItem: null,
    panel: 0,
    repairReasonCollection: {},
    selectedReason: null,
    temporaryItems: {},
    savedItems: {},
  };

  const updateState = (newState, deep = true) => {
    sidebarState = extend(deep, sidebarState, newState);
    renderSidebar();
  };

  const actions = {
    initialize: (item) => updateState(initializeSidebar(item), false),

    toggle: () => updateState({ open: !sidebarState.open }),

    toggleLoad: (loading) => updateState({ loading }),

    switchPanel: (panel) => updateState({ panel }),

    addRequiredField: (fieldName) => {
      updateState(
        {
          currentItem: {
            ...sidebarState.currentItem,
            requiredFields: [...(sidebarState.currentItem.requiredFields || []), fieldName],
          },
        },
        false,
      );
    },

    updateReasonCollection: (reasons) => {
      updateState({ repairReasonCollection: reasons });
    },

    updateReasons: (repairReasons) => {
      updateState(
        {
          currentItem: {
            ...sidebarState.currentItem,
            repairReasons: addCostToNames(repairReasons),
          },
        },
        false,
      );
    },

    selectReason: (reasonIndex, reason, multipleSelect) => {
      updateState(chooseReason(reasonIndex, reason, multipleSelect), false);
    },

    resetReason: (reasonIndex, reason) => {
      updateState({ currentItem: deselectReason(reasonIndex, reason) }, false);
    },

    formChange: (data, inputIndex) =>
      updateState({
        currentItem: { ...updateForm(data, inputIndex) },
      }),

    setLastEnabledSection: (sectionIndex) => {
      updateState(
        {
          currentItem: {
            ...sidebarState.currentItem,
            lastEnabledSection: sectionIndex,
          },
        },
        false,
      );
    },

    finalizeItem: async (additionalItem) => {
      updateState(await finalizeRepairItem(additionalItem), false);
    },

    deleteItem: () => updateState(deleteBlindItem(), false),

    resetSidebar: () => updateState({ open: false }),

    deletePhoto: () =>
      updateState({
        currentItem: {
          ...sidebarState.currentItem,
          item_images: [deletePhoto().imageName],
          repairImageUrl: '',
          repairPhotoData: null,
        },
      }),

    uploadFile: () => {
      const fileData = uploadFileData();
      updateState(getFileState(fileData));
    },

    toggleFileSizeError: (newState) => {
      updateState({
        currentItem: {
          ...sidebarState.currentItem,
          fileSizeExceeded: newState,
        },
      });
    },
  };

  const deletePhoto = () => {
    const img = document.getElementById('imageThumbnail');

    img.file = null;
    img.src = '/r2/img/icon_loading.svg';

    document.getElementById('fileInput').value = null;

    if (sidebarState.currentItem.item_images && sidebarState.currentItem.item_images[0]) {
      repairsUtils
        .deleteImage()(sidebarState.currentItem.item_images[0])
        .then(
          () => {},
          () => {},
        );
      sidebarState.currentItem.item_images.pop();
    }

    return { imageData: null, imageName: undefined };
  };

  const retakePhoto = () => {
    document.getElementById('fileInput').click();
  };

  const getFileState = (fileData) => {
    const fileSizeExceeded = fileData.imageData.size > 10485760;

    if (fileSizeExceeded) {
      actions.deletePhoto();

      return {
        currentItem: {
          ...sidebarState.currentItem,
          fileSizeExceeded,
        },
      };
    }
    return {
      currentItem: {
        ...sidebarState.currentItem,
        item_images: [fileData.imageName],
        repairPhotoData: fileData.imageData,
        repairImageUrl: '',
        fileSizeExceeded,
      },
    };
  };

  const uploadFileData = () => {
    const file = customerFileUpload.files[0];
    const img = document.getElementById('imageThumbnail');
    img.file = file;
    img.src = '/r2/img/icon_loading.svg';

    const reader = new FileReader();
    reader.onload = (function (aImg) {
      return function (e) {
        aImg.src = e.target.result;
      };
    })(img);
    reader.readAsDataURL(file);

    return { imageData: customerFileUpload.files[0], imageName: '' };
  };

  const getBlindItems = () => ({ ...sidebarState.savedItems });

  const getRepairReasons = (reasonCategory = '') => {
    const { repairReasonCollection } = sidebarState;

    if (!reasonCategory && Object.keys(repairReasonCollection === 0)) {
      repairsUtils
        .getReasonHierarchy()(reasonCategory)
        .then((data) => {
          const { response } = data;
          actions.updateReasonCollection(response);
          actions.updateReasons(response);
          actions.toggleLoad(false);
        })
        .catch((error) => console.error(error));
    }
  };

  const initializeSidebar = (item = {}) => {
    const { savedItems } = sidebarState;
    const mappedScopeItem = mapScopeItem(item);
    const newItem = Object.keys(item).length === 0;
    const itemInProgress = savedItems[mappedScopeItem.itemDescription];
    let itemInit;

    if (newItem) {
      itemInit = { ...mappedScopeItem };
      getRepairReasons();
    }

    return { currentItem: itemInProgress || itemInit };
  };

  const setUpdateCallback = (callback) => {
    updateCallback = callback;
  };

  const getReasonIdPath = (repairReasonPath, finalReasons) => {
    const repairReasons = repairReasonPath.filter(
      (reason) => reason.reason_code_id || Array.isArray(reason),
    );

    return repairReasons.map((step) => {
      if (Array.isArray(step)) {
        return step.map((substep) => substep.reason_code_id);
      }

      return finalReasons ? [step.reason_code_id] : step.reason_code_id;
    });
  };

  const reasonSelected = (reason, propName, formKey) => {
    const { currentItem } = sidebarState;
    let selectedReason = currentItem[formKey];
    const multipleReasons = Array.isArray(selectedReason);

    if (multipleReasons) {
      selectedReason = getReasonIdPath(selectedReason);
      return selectedReason.indexOf(reason.reason_code_id) > -1;
    }

    return selectedReason;
  };

  const getReasonCost = (reason) => {
    const reasonSettings = reason.reason_settings || {};
    const reasonAttributes = reasonSettings.attributes || {};
    const reasonCost = reasonAttributes.cost;
    return reasonCost && parseInt(reasonCost, 10);
  };

  const addCostToNames = (reasons = []) =>
    reasons.map((reason) => {
      const reasonCost = getReasonCost(reason);
      let costDisplay = reasonCost === 0 ? 'Free' : reasonCost;

      if (costDisplay === 0 || costDisplay !== undefined) {
        costDisplay = costDisplay === 'Free' ? costDisplay : `$${costDisplay}`;
        return { ...reason, display_name: `${reason.display_name} (${costDisplay})` };
      }

      return reason;
    });

  const chooseReason = (reasonIndex, inputKey, multipleSelect) => {
    const { currentItem } = sidebarState;
    const { reasonPath, repairReasons, requiredFields } = currentItem;
    const previousSelection = currentItem[inputKey] || [];
    const selectedReason = { ...repairReasons[reasonIndex] };
    const selectedDisplayName = selectedReason.display_name;
    const nextLevelReasons = selectedReason.reasons;
    const reasonCost = getReasonCost(selectedReason);
    const update = {
      currentItem: {
        ...currentItem,
        [inputKey]: multipleSelect ? [...previousSelection, selectedReason] : selectedReason,
        reasonPath: [...reasonPath, selectedReason],
        repairReasons: nextLevelReasons ? addCostToNames(nextLevelReasons) : repairReasons,
        lastEnabledSection: null,
        ...(inputKey === 'repairItemCategory' ? { itemCategory: selectedDisplayName } : {}),
        requiredFields: inputKey === 'repairItemCategory' ? [] : requiredFields,
      },
    };

    if (reasonCost) {
      update.currentItem.repairCost = reasonCost;
    }

    return update;
  };

  const deselectReason = (reasonIndex, inputKey) => {
    const { currentItem, repairReasonCollection } = sidebarState;
    const { reasonPath, repairReasons, requiredFields } = currentItem;
    const selection = currentItem[inputKey];
    const selectedReasonId = selection.reason_code_id;
    const multipleReasons = Array.isArray(selection);
    const deleteFields = requiredFields.slice(requiredFields.indexOf(inputKey));
    deleteFields.forEach((field) => delete currentItem[field]);
    let newReasonTier = [...repairReasonCollection];
    let newPathEndIndex = 0;
    let newReasonPath;
    let newReasonSelections;

    if (!multipleReasons) {
      reasonPath.forEach((step, stepIndex) => {
        const stepId = step.reason_code_id;
        const endPathTrace = stepId === selectedReasonId;

        if (!endPathTrace) {
          const nodeMatch = newReasonTier.filter((reason) => {
            const reasonId = reason.reason_code_id;
            const pathEndFound = reasonId === stepId;

            if (pathEndFound) {
              newPathEndIndex = stepIndex + 1;
            }

            return pathEndFound;
          })[0];

          const nextLevelReasons = nodeMatch ? nodeMatch.reasons : [];
          newReasonTier = nextLevelReasons.length > 0 ? nextLevelReasons : newReasonTier;
        }
      });

      newReasonPath = reasonPath.slice(0, newPathEndIndex);
    } else {
      const clickedReasonId = repairReasons[reasonIndex].reason_code_id;
      newReasonSelections = selection.filter((reason) => reason.reason_code_id !== clickedReasonId);
    }

    return {
      ...currentItem,
      repairReasons: newReasonSelections ? repairReasons : addCostToNames(newReasonTier),
      reasonPath: newReasonPath || reasonPath,
      lastEnabledSection: null,
      repairCost: 0,
      [inputKey]: newReasonSelections || currentItem[inputKey],
    };
  };

  const updateForm = (inputData, inputIndex) => {
    const { currentItem } = sidebarState;
    const { requiredFields = [] } = currentItem;
    const { name, value } = inputData.target;
    const trimmedValue = value.trim();
    const requiredField = requiredFields.indexOf(name) >= 0;
    const newEnabledSection = requiredField && !trimmedValue ? inputIndex : null;
    return { [name]: !trimmedValue ? '' : value, lastEnabledSection: newEnabledSection };
  };

  const checkDisabledSection = (requiredInput, sectionIndex) => {
    const { currentItem } = sidebarState;
    const { lastEnabledSection } = currentItem;
    const storedValue = currentItem[requiredInput];
    const emptyMultiSelect = Array.isArray(storedValue) && !storedValue.length;
    const emptyRequiredField = !storedValue || emptyMultiSelect;

    if (emptyRequiredField && lastEnabledSection === null) {
      actions.setLastEnabledSection(sectionIndex);
    }
  };

  const buildFormState = (formComponent) => {
    const { inputKey, required } = formComponent;
    const {
      currentItem: { requiredFields = [] },
    } = sidebarState;

    if (required && requiredFields.indexOf(inputKey) < 0) {
      actions.addRequiredField(inputKey);
    }
  };

  const checkRequiredFields = () => {
    const currentItem = sidebarState.currentItem || {};
    const requiredFields = currentItem.requiredFields || [];

    return requiredFields.reduce((accumulator, current) => {
      const requiredValue = currentItem[current];
      const emptyValue = Array.isArray(requiredValue) && requiredValue.length === 0;
      const invalidValue = !requiredValue || emptyValue;
      return invalidValue || accumulator;
    }, false);
  };

  const checkCommentField = () => {
    const currentItem = sidebarState.currentItem || {};
    const requiredFields = currentItem.requiredFields || [];
    let commentMandatory;

    requiredFields.forEach((field) => {
      const fieldData = currentItem[field] || {};

      if (fieldData.reason_code_id) {
        const reasonSettings = fieldData.reason_settings || {};
        const mandatoryFields = reasonSettings.mandatory_fields || [];
        commentMandatory = mandatoryFields.indexOf('comments') >= 0;
      }
    });

    return commentMandatory && !currentItem.itemComment;
  };

  const mapFinalReasons = () => {
    const { currentItem } = sidebarState;
    const { requiredFields = [] } = currentItem;
    const chosenReasons = requiredFields.map((field) => currentItem[field]);
    return getReasonIdPath(chosenReasons, true);
  };

  const finalizeRepairItem = async (additionalItems) => {
    const { currentItem, savedItems } = sidebarState;
    const { itemDescription } = currentItem;
    const imageData = currentItem.repairPhotoData;
    const previouslySavedItem = savedItems[itemDescription];
    const finalRepairItem = {
      ...currentItem,
      ...mapFinalRepair(currentItem),
      repair_reason_code_ids: mapFinalReasons(),
    };

    if (previouslySavedItem) {
      actions.deleteItem();
    }

    if (imageData) {
      const { filename, path } = await repairsUtils.uploadImage()(imageData);
      finalRepairItem.repairPhotoData = null;
      finalRepairItem.item_images[0] = filename;
      finalRepairItem.repairImageUrl = `${path}${filename}`;
    }

    returnItemsService.addItem(finalRepairItem);
    const itemUpdate = { ...savedItems, [itemDescription]: finalRepairItem };
    updateCallback(itemUpdate, additionalItems);

    return { currentItem: {}, savedItems: itemUpdate };
  };

  const deleteBlindItem = () => {
    const { currentItem, savedItems } = sidebarState;
    const { itemDescription } = currentItem;
    returnItemsService.removeItemByName(itemDescription);
    delete savedItems[itemDescription];
    updateCallback({ ...savedItems }, true);
    return { savedItems: { ...savedItems } };
  };

  const handleDismissOnClick = () => {
    actions.toggleFileSizeError(false);
  };

  const renderSidebar = async () => {
    const module = await import(
      /* webpackChunkName: "blind-repair-sidebar" */ `../../components/BlindRepairSidebar/blindRepairSidebar.js`
    );
    module.renderBlindRepairSidebar({
      sidebarState,
      checkCommentField,
      node,
      checkRequiredFields,
      buildFormState,
      handleDismissOnClick,
      checkDisabledSection,
    });
  };

  return {
    actions,
    setUpdateCallback,
    getBlindItems,
  };
};

export const blindRepairSidebar = blindRepairSidebarFunction();
